import { Component, OnInit, ViewChild } from '@angular/core';
import { SalesService } from '../../../_services/sales/sales.service';
import { SessionService } from '../../../_services/session/session.service';
import { ValidatorService } from '../../../_services/validators/validator.service';
import { PatientSearcherComponent } from '../../patient-searcher/patient-searcher.component';
import { MessageService } from 'primeng/api';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import { SelectorPatientTreatmentComponent } from '../../selectors/selector-patient-treatment/selector-patient-treatment.component';
import IPatientHasTreatment from '../../../_interfaces/IPatientHasTreatment';
import IPatient from '../../../_interfaces/IPatients';
import IProduct from '../../../_interfaces/IProduct';
import ISale from '../../../_interfaces/ISale';
import IPaymentTypes from '../../../_interfaces/IPaymentTypes';
import { ProductImageComponent } from '../../product-image/product-image.component';
import { InputNumberModule } from 'primeng/inputnumber';
import { FormsModule } from '@angular/forms';
import { DatePickerModule } from 'primeng/datepicker';
import { SelectorPaymentTypeComponent } from '../../selectors/selector-payment-type/selector-payment-type.component';
import { CommonModule } from '@angular/common';
import { SelectorSiteProductsComponent } from '../../selectors/selector-site-products/selector-site-products.component';
import { ButtonModule } from 'primeng/button';
import { FormBase } from '../../../_classes/form-base.class';
import { Observable } from 'rxjs';
import { EResources } from '../../../_resources/EResources';

type SaleWizardStep = 'product' | 'payment' | 'association';

@Component({
  standalone: true,
  selector: 'app-form-sale',
  templateUrl: './form-sale.component.html',
  styleUrls: ['./form-sale.component.scss'],
  imports: [
    CommonModule,
    FormsModule,
    TranslatePipe,
    ButtonModule,
    InputNumberModule,
    DatePickerModule,
    ProductImageComponent,
    SelectorSiteProductsComponent,
    SelectorPaymentTypeComponent,
    SelectorPatientTreatmentComponent,
    PatientSearcherComponent,
  ],
})
export class FormSaleComponent extends FormBase<ISale> implements OnInit {
  @ViewChild('patientSearcher')
  patientSearcher: PatientSearcherComponent;
  @ViewChild('siteProductsSelector')
  siteProductsSelector: SelectorSiteProductsComponent;
  @ViewChild('patientsTreatmentsSelector')
  patientsTreatmentsSelector: SelectorPatientTreatmentComponent;
  @ViewChild('paymentMethodSelector')
  paymentMethodSelector: SelectorPaymentTypeComponent;

  readonly wizardOrder: SaleWizardStep[] = [
    'product',
    'payment',
    'association',
  ];

  public currentStep: SaleWizardStep = 'product';
  public patient: IPatient;
  public product: IProduct;
  public paymentType: IPaymentTypes;
  public treatment: IPatientHasTreatment;
  public defaultProuductImg = 'assets/images/default/product.png';
  public salesDate = new Date();

  constructor(
    public session: SessionService,
    public saleService: SalesService,
    public validator: ValidatorService,
    public messageService: MessageService,
    public translate: TranslateService,
  ) {
    super(session, messageService, translate);
  }

  get currentStepIndex(): number {
    return Math.max(0, this.wizardOrder.indexOf(this.currentStep));
  }

  get progressPercent(): number {
    return Math.round(
      ((this.currentStepIndex + 1) / this.wizardOrder.length) * 100,
    );
  }

  get canGoBack(): boolean {
    return this.currentStepIndex > 0;
  }

  get isLastStep(): boolean {
    return this.currentStepIndex === this.wizardOrder.length - 1;
  }

  ngOnInit(): void {
    super.ngOnInit();

    if (this.payload) {
      this.salesDate = this.payload.sales_date
        ? new Date(this.payload.sales_date)
        : new Date();
      this.product = this.payload.product;
      this.paymentType = this.payload.paymentType;
      this.patient = this.payload.patient;
      this.treatment = this.payload.treatment as unknown as IPatientHasTreatment;
    }
  }

  clear(): void {
    this.currentStep = 'product';
    this.salesDate = new Date();
    this.product = undefined;
    this.paymentType = undefined;
    this.patient = undefined;
    this.treatment = undefined;

    if (this.siteProductsSelector) this.siteProductsSelector.clear();
    if (this.patientSearcher) this.patientSearcher.clear();
    if (this.patientsTreatmentsSelector) {
      this.patientsTreatmentsSelector.clear();
    }
    if (this.paymentMethodSelector) this.paymentMethodSelector.clear();

    this.payload = {
      sites_id: null,
      product: undefined,
      products_id: null,
      payments_types_id: null,
      treatments_id: null,
      sellers_id: null,
      price: undefined,
      patients_id: null,
      sales_date: new Date(),
    };
  }

  generatePayload(): ISale {
    return {
      sites_id: this.sites_id,
      products_id: this.resolveSelectedProductId(),
      payments_types_id: this.paymentType?.id,
      treatments_id: this.treatment?.id,
      sellers_id: this.users_id,
      price: this.payload.price,
      patients_id: this.patient?.id,
      sales_date: this.salesDate,
    };
  }

  getResourceName(): string {
    return EResources.SALES;
  }

  properties(): string[] {
    return [
      'sites_id',
      'products_id',
      'payments_types_id',
      'treatments_id',
      'sellers_id',
      'price',
      'patients_id',
      'sales_date',
      'product',
      'paymentType',
      'treatment',
      'seller',
    ];
  }

  validate(): Promise<void> {
    const sale = this.generatePayload();
    return this.validator.sale(sale);
  }

  goToPreviousStep(): void {
    if (!this.canGoBack) return;

    this.currentStep = this.wizardOrder[this.currentStepIndex - 1];
  }

  goToNextStep(): void {
    if (!this.validateStep(this.currentStep)) {
      this.showStepValidationHint();
      return;
    }

    const nextStep = this.wizardOrder[this.currentStepIndex + 1];
    if (nextStep) {
      this.currentStep = nextStep;
    }
  }

  validateStep(step: SaleWizardStep): boolean {
    switch (step) {
      case 'product':
        return this.validateProductStep();
      case 'payment':
        return this.validatePaymentStep();
      case 'association':
        return true;
      default:
        return false;
    }
  }

  handleProduct(product?: IProduct) {
    if (!product) {
      this.product = undefined;
      this.payload.product = undefined;
      this.payload.products_id = null;
      return;
    }

    this.product = product;
    this.payload.product = product;
    this.payload.products_id = this.resolveProductId(product);

    if (this.mode !== 'edit') {
      this.payload.price = Number(product.prices?.[0]?.price || 0);
    }
  }

  handlePaymentMethod(paymentType?: IPaymentTypes) {
    this.paymentType = paymentType;
    this.payload.paymentType = paymentType;
    this.payload.payments_types_id = paymentType?.id || null;
  }

  handlePatient(patient?: IPatient) {
    this.patient = patient;
    this.payload.patient = patient;
    this.payload.patients_id = patient?.id || null;
    this.treatment = undefined;
    this.payload.treatment = undefined;
    this.payload.treatments_id = null;

    if (!patient && this.patientsTreatmentsSelector) {
      this.patientsTreatmentsSelector.clear();
    }
  }

  handleTreatment(treatment?: IPatientHasTreatment) {
    this.treatment = treatment;
    this.payload.treatment = treatment as any;
    this.payload.treatments_id = treatment?.id || null;
  }

  clearAssociation(): void {
    this.handlePatient(undefined);
    if (this.patientSearcher) this.patientSearcher.clear();
  }

  createFn(payload: ISale): Observable<any> {
    return this.saleService.create(this.customers_id, this.sites_id, payload);
  }

  updateFn(payload: ISale): Observable<any> {
    return this.saleService.update(
      this.customers_id,
      this.sites_id,
      this.payload.id,
      payload,
    );
  }

  private validateProductStep(): boolean {
    let valid = true;

    this.errors.product = undefined;
    this.errors.price = undefined;

    if (!this.resolveSelectedProductId()) {
      this.errors.product = 'ERRORS.SALE.PRODUCT';
      valid = false;
    }

    if (this.payload.price === undefined || this.payload.price === null) {
      this.errors.price = 'ERRORS.SALE.PRICE';
      valid = false;
    }

    return valid;
  }

  private validatePaymentStep(): boolean {
    let valid = true;

    this.errors.sales_date = undefined;
    this.errors.paymentType = undefined;

    if (!this.salesDate || Number.isNaN(this.salesDate.valueOf())) {
      this.errors.sales_date = 'ERRORS.SALE.DATE';
      valid = false;
    } else if (this.salesDate.valueOf() > new Date().valueOf()) {
      this.errors.sales_date = 'ERRORS.SALE.DATE';
      valid = false;
    }

    if (!this.paymentType?.id) {
      this.errors.paymentType = 'ERRORS.SALE.PAYMENT_TYPE';
      valid = false;
    }

    return valid;
  }

  private showStepValidationHint(): void {
    this.messageService.add({
      severity: 'warn',
      summary: 'Completa este paso',
      detail:
        this.currentStep === 'product'
          ? 'Selecciona un producto e indica el precio antes de continuar.'
        : 'Define la fecha de venta y el método de pago antes de continuar.',
    });
  }

  private resolveSelectedProductId(): number | undefined {
    return this.resolveProductId(this.product);
  }

  private resolveProductId(product?: IProduct): number | undefined {
    const resolved = Number(product?.products_id || product?.id || 0);
    return resolved > 0 ? resolved : undefined;
  }
}
